import api from './api';

export interface AiFoodCacheItem {
  id: number;
  nameKey: string;
  displayName: string;
  foodType: 'food' | 'liquid' | 'unrecognized' | null;
  unit: 'г' | 'мл' | null;
  portionName: string | null;
  portionSize: number | null;
  caloriesPer100: number | null;
  proteinPer100: number | null;
  fatPer100: number | null;
  carbsPer100: number | null;
  fiberPer100: number | null;
  source: 'ai' | 'manual';
  hitCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AiWorkoutCacheItem {
  id: number;
  nameKey: string;
  displayName: string;
  workoutType: 'strength' | 'cardio' | 'stretching' | 'unrecognized' | null;
  unit: string | null;
  intensity: string; // low / medium / high; '' - не вказано (частина ключа кешу)
  caloriesPerUnit: number | null;
  metValue: number | null;
  workoutId: number | null;
  matchScore: number | null;
  metActivityId: number | null;
  calcMethod: 'library_match' | 'met_db' | 'ai_estimate' | null;
  metCandidateCodes: string | null;
  aiEstimateReason: 'no_candidates' | 'rejected' | 'invalid_code' | 'no_duration' | null;
  minutesPerUnit: number | null;
  kcalPerKgPerUnit: number | null;
  referenceWeightKg: number | null;
  source: 'ai' | 'manual';
  hitCount: number;
  createdAt: string;
  updatedAt: string;
}

export type MetMissReason = 'no_candidates' | 'rejected' | 'invalid_code' | 'no_duration';

/** "Not found in MET" journal row (one per name + unit + intensity + reason). */
export interface AiWorkoutMetMiss {
  id: number;
  nameKey: string;
  displayName: string;
  unit: string;
  intensity: string;
  reason: MetMissReason;
  occurrences: number;
  candidateCodes: string | null;
  topScore: string | null;
  aiMetCode: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface AiWorkoutMetMissesParams {
  page: number;
  limit: number;
  search?: string;
  reason?: MetMissReason;
}

export interface AiFoodExtractCacheItem {
  id: number;
  textKey: string;
  originalText: string;
  locale: string;
  suggestedTitle: string;
  items: AiFoodExtractedItem[];
  source: 'ai' | 'manual';
  hitCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AiFoodExtractedItem {
  type: 'food' | 'liquid' | 'workout' | 'unrecognized';
  name: string;
  totalAmount: number;
  unit: 'г' | 'мл';
  portionName?: string;
  portionSize?: number;
  portionCount?: number;
  originalText?: string;
}

export interface AiCacheListParams {
  page?: number;
  limit?: number;
  search?: string;
  source?: 'ai' | 'manual' | '';
}

export interface AiWorkoutCacheListParams extends AiCacheListParams {
  workoutType?: string;
}

export interface AiCacheListResponse<T> {
  data: T[];
  meta: { total: number; page: number; limit: number };
}

export interface AiWorkoutExtractCacheItem {
  id: number;
  textKey: string;
  originalText: string;
  locale: string;
  suggestedTitle: string;
  items: AiWorkoutExtractedItem[];
  source: 'ai' | 'manual';
  hitCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AiWorkoutExtractedItem {
  type: 'strength' | 'cardio' | 'stretching' | 'unrecognized';
  name: string;
  quantity: number;
  unit: 'повторення' | 'хв' | 'км' | 'м' | 'секунди' | 'підходи';
  intensity?: 'low' | 'medium' | 'high';
  originalText?: string;
}

export interface UpdateWorkoutExtractCachePayload {
  suggestedTitle?: string;
  items?: AiWorkoutExtractedItem[];
  source?: 'ai' | 'manual';
}

export interface AiMedicationExtractCacheItem {
  id: number;
  textKey: string;
  originalText: string;
  locale: string;
  suggestedTitle: string;
  items: AiMedicationExtractedItem[];
  source: 'ai' | 'manual';
  hitCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AiMedicationExtractedItem {
  name: string;
  quantity: number;
  unit: 'таблетка' | 'капсула' | 'мг' | 'мл' | 'крапля' | 'порція' | 'шт';
  originalText?: string;
}

export interface UpdateMedicationExtractCachePayload {
  suggestedTitle?: string;
  items?: AiMedicationExtractedItem[];
  source?: 'ai' | 'manual';
}

export interface UpdateFoodExtractCachePayload {
  suggestedTitle?: string;
  items?: AiFoodExtractedItem[];
  source?: 'ai' | 'manual';
}

class AdminAiCacheService {
  async getFoodExtractCache(params: AiCacheListParams): Promise<AiCacheListResponse<AiFoodExtractCacheItem>> {
    const response = await api.get('/admin/ai-cache/food-extracts', {
      params: { ...params, source: params.source || undefined },
    });
    return { data: response.data.data, meta: response.data.meta };
  }

  async updateFoodExtractCache(id: number, data: UpdateFoodExtractCachePayload): Promise<AiFoodExtractCacheItem> {
    const response = await api.patch(`/admin/ai-cache/food-extracts/${id}`, data);
    return response.data.data;
  }

  async deleteFoodExtractCache(id: number): Promise<void> {
    await api.delete(`/admin/ai-cache/food-extracts/${id}`);
  }

  async getMedicationExtractCache(params: AiCacheListParams): Promise<AiCacheListResponse<AiMedicationExtractCacheItem>> {
    const response = await api.get('/admin/ai-cache/medication-extracts', {
      params: { ...params, source: params.source || undefined },
    });
    return { data: response.data.data, meta: response.data.meta };
  }

  async updateMedicationExtractCache(id: number, data: UpdateMedicationExtractCachePayload): Promise<AiMedicationExtractCacheItem> {
    const response = await api.patch(`/admin/ai-cache/medication-extracts/${id}`, data);
    return response.data.data;
  }

  async deleteMedicationExtractCache(id: number): Promise<void> {
    await api.delete(`/admin/ai-cache/medication-extracts/${id}`);
  }

  async getWorkoutExtractCache(params: AiCacheListParams): Promise<AiCacheListResponse<AiWorkoutExtractCacheItem>> {
    const response = await api.get('/admin/ai-cache/workout-extracts', {
      params: { ...params, source: params.source || undefined },
    });
    return { data: response.data.data, meta: response.data.meta };
  }

  async updateWorkoutExtractCache(id: number, data: UpdateWorkoutExtractCachePayload): Promise<AiWorkoutExtractCacheItem> {
    const response = await api.patch(`/admin/ai-cache/workout-extracts/${id}`, data);
    return response.data.data;
  }

  async deleteWorkoutExtractCache(id: number): Promise<void> {
    await api.delete(`/admin/ai-cache/workout-extracts/${id}`);
  }

  async getFoodCache(params: AiCacheListParams): Promise<AiCacheListResponse<AiFoodCacheItem>> {
    const response = await api.get('/admin/ai-cache/foods', {
      params: {
        ...params,
        source: params.source || undefined,
      },
    });
    return { data: response.data.data, meta: response.data.meta };
  }

  async updateFoodCache(id: number, data: Partial<AiFoodCacheItem>): Promise<AiFoodCacheItem> {
    const response = await api.patch(`/admin/ai-cache/foods/${id}`, data);
    return response.data.data;
  }

  async updateWorkoutCache(id: number, data: Partial<AiWorkoutCacheItem>): Promise<AiWorkoutCacheItem> {
    const response = await api.patch(`/admin/ai-cache/workouts/${id}`, data);
    return response.data.data;
  }

  /** Clears the workout calories cache together with the "not found in MET" journal (not the extraction cache). */
  async clearWorkoutCache(): Promise<{ deleted: number; missesDeleted: number }> {
    const response = await api.delete('/admin/ai-cache/workouts');
    return { deleted: response.data.data?.deleted ?? 0, missesDeleted: response.data.data?.missesDeleted ?? 0 };
  }

  /** Deletes one workout cache row; the next request recalculates it. */
  async deleteWorkoutCacheItem(id: number): Promise<void> {
    await api.delete(`/admin/ai-cache/workouts/${id}`);
  }

  /** Clears only the "not found in MET" journal. */
  async clearWorkoutMetMisses(): Promise<number> {
    const response = await api.delete('/admin/ai-cache/workouts/met-misses');
    return response.data.data?.deleted ?? 0;
  }

  /** "Not found in MET" journal, most frequent first. */
  async getWorkoutMetMisses(params: AiWorkoutMetMissesParams): Promise<{ data: AiWorkoutMetMiss[]; meta: { total: number; page: number; limit: number } }> {
    const response = await api.get('/admin/ai-cache/workouts/met-misses', {
      params: { ...params, search: params.search || undefined, reason: params.reason || undefined },
    });
    return { data: response.data.data, meta: response.data.meta };
  }

  /** Removes AI estimates from the cache: they are recalculated against the MET base on the next request. */
  async clearWorkoutAiEstimates(): Promise<number> {
    const response = await api.delete('/admin/ai-cache/workouts/ai-estimates');
    return response.data.data?.deleted ?? 0;
  }

  /** Downloads the whole workout cache as XLSX (with library workout and MET when matched). */
  async exportWorkoutCache(): Promise<void> {
    const response = await api.get('/admin/ai-cache/workouts/export', { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `ai-workout-cache-${new Date().toISOString().slice(0, 10)}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async getWorkoutCache(params: AiWorkoutCacheListParams): Promise<AiCacheListResponse<AiWorkoutCacheItem>> {
    const response = await api.get('/admin/ai-cache/workouts', {
      params: {
        ...params,
        source: params.source || undefined,
        workoutType: params.workoutType || undefined,
      },
    });
    return { data: response.data.data, meta: response.data.meta };
  }
}

export const adminAiCacheService = new AdminAiCacheService();
